import { useAppSelector } from "@/app/hooks";
import { useAuthService } from "@/services/authentication";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldList, EmeraldListItem } from "@emerald-react/list";
import { EmeraldMenu } from "@emerald-react/menu";
import type { UserMenuProps } from "@shared/types/component.types";
import classNames from "classnames";
import { useEffect } from "react";

const UserMenu = ({
  setToggleHeader,
  toggleHeader,
  setAnimationClass,
}: UserMenuProps) => {
  const { handleSignOut } = useAuthService();
  const user = useAppSelector((state) => state.user);

  // Migration: Transfer old localStorage key to new key on component mount
  useEffect(() => {
    const oldValue = localStorage.getItem("emerald-app-shell");
    const newValue = localStorage.getItem("showAppShell");

    // If old key exists and new key doesn't, migrate the value
    if (oldValue !== null && newValue === null) {
      localStorage.setItem("showAppShell", oldValue);
      localStorage.removeItem("emerald-app-shell");
      console.info(
        "Migrated app shell preference from emerald-app-shell to showAppShell",
      );
    }
  }, []);

  const onSignOut = () => {
    try {
      handleSignOut();
    } catch (_error) {
      console.error("Sign out failed");
    }
  };

  const toggleHeaderState = () => {
    setAnimationClass(!toggleHeader ? "slideInDown" : "slideOutUp");
    setTimeout(() => {
      const newValue = String(!toggleHeader);
      localStorage.setItem("showAppShell", newValue);

      // Dispatch custom event to notify the SharePanel
      window.dispatchEvent(
        new CustomEvent("showAppShell-changed", {
          detail: { value: newValue },
        }),
      );

      setToggleHeader((prev: boolean) => !prev);
    }, 500);
  };

  return (
    <section
      className={classNames("flex space-x-2", {
        "fixed-user-menu": !toggleHeader,
      })}
    >
      {toggleHeader && (
        <div>Welcome, {user?.displayName || user?.firstName}</div>
      )}
      <EmeraldMenu
        triggerElement={
          <EmeraldIcon
            icon="settings"
            className="animated slideInDown"
            type="filled"
          />
        }
        position="bottom_end"
      >
        <EmeraldList>
          <EmeraldListItem
            label="Show App Shell"
            prefixType="icon"
            iconOptions={{ icon: toggleHeader ? "check" : undefined }}
            classes="hover:text-white hover:bg-primary"
            onClick={toggleHeaderState}
          />
          <EmeraldListItem
            label="Logout"
            prefixType="icon"
            onClick={onSignOut}
            iconOptions={{ icon: "exit_to_app" }}
            classes="hover:text-white hover:bg-primary"
          />
        </EmeraldList>
      </EmeraldMenu>
    </section>
  );
};

// Add displayName for better debugging
UserMenu.displayName = "UserMenu";

export default UserMenu;
